import { useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import type { Trip, Segment } from "../types";
import type { TimeWindow } from "../utils/entityActive";
import type { SegmentWithId } from "./useSegments";
import { addDaysToKey, dayKeyOf, localMidnightOfKey } from "../utils/calendarDay";

/** Everything is active when there's nothing to date the question against. */
const UNBOUNDED: TimeWindow = { fromMs: null, toMs: null };

/** A trip's or segment's calendar days, from the first day's local midnight
 *  to the end of the last, on this device's clock. */
function windowFrom(range: { startDate?: Segment["startDate"]; endDate?: Segment["endDate"] } | null) {
  const start = dayKeyOf(range?.startDate);
  const end = dayKeyOf(range?.endDate);
  if (!start || !end) return null;
  const endOfLastDay = localMidnightOfKey(addDaysToKey(end, 1)).getTime() - 1;
  return { fromMs: localMidnightOfKey(start).getTime(), toMs: endOfLastDay };
}

interface UseActiveAsOfOptions {
  /** The trip being logged into, when there is one. */
  tripId?: string | null;
  /** The segment being logged into — preferred over the trip when set. */
  segmentId?: string | null;
  /** Already-loaded segments, to save re-reading one the caller has in hand. */
  segments?: SegmentWithId[];
}

/**
 * The stretch of time an entity picker should judge "is this a real place" by.
 *
 * This is the piece that would otherwise drift. Every surface that filters
 * entities has different things in hand — `LogActivityPicker` has the segment
 * list, `Entities` and `EntityDetail` have only ids in the URL, nearby has
 * nothing — and each would reach for a different answer, leaving the search
 * dropdown and the nearby strip below it disagreeing about whether a house
 * exists. They all call this instead.
 *
 * Narrowest context wins: the selected segment's dates, else the trip's, else
 * today. With no trip context at all the window is unbounded, which is how the
 * standalone places list keeps showing everything ever built.
 */
export function useActiveAsOf({ tripId, segmentId, segments }: UseActiveAsOfOptions): TimeWindow {
  const preloaded = useMemo(
    () => (segmentId ? segments?.find((s) => s.id === segmentId) ?? null : null),
    [segments, segmentId],
  );

  const [fetchedWindow, setFetchedWindow] = useState<TimeWindow | null>(null);

  // Only reaches Firestore for context the caller couldn't supply — the log
  // sheet passes its segments in, so picking a segment costs no read.
  const needsFetch = !!tripId && !preloaded;
  useEffect(() => {
    if (!needsFetch || !tripId) {
      setFetchedWindow(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        if (segmentId) {
          const snap = await getDoc(doc(db, "trips", tripId, "segments", segmentId));
          const w = snap.exists() ? windowFrom(snap.data() as Segment) : null;
          if (!cancelled && w) {
            setFetchedWindow(w);
            return;
          }
        }
        const tripSnap = await getDoc(doc(db, "trips", tripId));
        if (cancelled) return;
        setFetchedWindow(tripSnap.exists() ? windowFrom(tripSnap.data() as Trip) : null);
      } catch {
        // A trip we can't read shouldn't hide places; fall through to "now".
        if (!cancelled) setFetchedWindow(null);
      }
    })();
    return () => { cancelled = true; };
  }, [needsFetch, tripId, segmentId]);

  return useMemo(() => {
    const fromSegment = windowFrom(preloaded);
    if (fromSegment) return fromSegment;
    if (fetchedWindow) return fetchedWindow;
    // Trip context exists but its dates haven't arrived (or wouldn't load):
    // judge by today rather than by nothing, so opening the log sheet on a
    // live trip doesn't flash last year's houses before settling.
    if (tripId) {
      const now = Date.now();
      return { fromMs: now, toMs: now };
    }
    return UNBOUNDED;
  }, [preloaded, fetchedWindow, tripId]);
}

/** The "right now" window, for surfaces with no trip context of their own. */
export function nowWindow(): TimeWindow {
  const now = Date.now();
  return { fromMs: now, toMs: now };
}
